
import { useCallback } from "react";
import { useConsolidatedProjectHandling } from "./uppdragPage/useConsolidatedProjectHandling";
import { useLoadingState } from "./uppdragPage/useLoadingState";
import { useFolderHandling } from "./uppdragPage/useFolderHandling";
import { useTabState } from "./uppdragPage/useTabState";
import { useAssessmentHandling } from "./uppdragPage/useAssessmentHandling";

export interface UppdragPageReturn {
  selectedFolderId: string | null;
  projectId: string | null;
  projectName: string;
  isLoading: boolean;
  initialLoadComplete: boolean;
  activeTab: string;
  activeSubTab: string;
  isSharedFolder: boolean;
  fetchError: string | null;
  hasProjects: boolean | null;
  folderTreeKey: number;
  setActiveTab: (tab: string) => void;
  setActiveSubTab: (tab: string) => void;
  handleSelectProject: (projectId: string) => Promise<string>;
  handleSelectSharedFolder: (folderId: string) => Promise<string>;
  handleCreateRootFolder: () => boolean;
  handleStartAssessment: (projectId: string, folderId: string) => void;
  handleBackToFolders: () => void;
  handleSelectFolder: (folderId: string) => void;
  handleNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  handleCreateNewAssessment: (projectId: string, folderId: string) => void;
  fetchDefaultProject: () => Promise<boolean>;
  fetchProjectIdForSharedFolder: (folderId: string) => Promise<string | null>;
  refreshFolderTree: () => void;
}

export const useUppdragPage = (): UppdragPageReturn => {
  const { isLoading: globalLoading, setIsLoading } = useLoadingState();
  
  const {
    projectId,
    projectName,
    fetchError,
    hasProjects,
    isLoading: projectLoading,
    initialLoadComplete,
    handleSelectProject: handleSelectProjectBase,
    fetchDefaultProject: originalFetchDefaultProject
  } = useConsolidatedProjectHandling();
  
  const {
    selectedFolderId,
    isSharedFolder,
    folderTreeKey,
    handleSelectFolder,
    handleSelectSharedFolder,
    handleCreateRootFolder,
    handleBackToFolders,
    fetchProjectIdForSharedFolder,
    refreshFolderTree
  } = useFolderHandling(projectId);
  
  const {
    activeTab,
    activeSubTab,
    setActiveTab,
    setActiveSubTab
  } = useTabState();

  // Use the assessment handling hook for unified navigation
  const { handleCreateNewAssessment, handleNavigateToAssessment: handleNavigateToAssessmentBase } = useAssessmentHandling();

  // Simplified loading state - only use project loading from consolidated handler
  const isLoading = projectLoading;

  // Wrap fetchDefaultProject to handle loading state
  const fetchDefaultProject = useCallback(async () => {
    setIsLoading(true);
    try {
      return await originalFetchDefaultProject();
    } finally {
      setIsLoading(false);
    }
  }, [originalFetchDefaultProject, setIsLoading]);

  // Wrap handleSelectProject to make it async
  const handleSelectProject = useCallback(async (projectId: string) => {
    return await handleSelectProjectBase(projectId);
  }, [handleSelectProjectBase]);

  // Use the new assessment handling for creating new assessments
  const handleStartAssessment = useCallback((projectId: string, folderId: string) => {
    console.log("Starting NEW assessment:", { projectId, folderId });
    handleCreateNewAssessment(projectId, folderId);
  }, [handleCreateNewAssessment]);

  // Use the new assessment handling for navigating to existing assessments
  const handleNavigateToAssessment = useCallback((assessmentId: string, folderId: string) => {
    console.log("Navigating to EXISTING assessment:", { assessmentId, folderId });
    handleNavigateToAssessmentBase(assessmentId, folderId, projectId || undefined);
  }, [handleNavigateToAssessmentBase, projectId]);

  return {
    // State
    selectedFolderId,
    projectId,
    projectName,
    isLoading,
    initialLoadComplete,
    activeTab,
    activeSubTab,
    isSharedFolder,
    fetchError,
    hasProjects,
    folderTreeKey,
    
    // Actions
    setActiveTab,
    setActiveSubTab,
    handleSelectProject,
    handleSelectSharedFolder,
    handleCreateRootFolder,
    handleStartAssessment,
    handleBackToFolders,
    handleSelectFolder,
    handleNavigateToAssessment,
    handleCreateNewAssessment,
    fetchDefaultProject,
    fetchProjectIdForSharedFolder,
    refreshFolderTree
  };
};
